// /redaktionell-policy — E-E-A-T-sida: vem som skriver, hur vi granskar,
// vilka källor vi använder och hur man anmäler fel. Länkas från varje byline.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { breadcrumbs, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, ORG_ID, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { CONTACT } from "@/content/contact";
import {
  EDITORIAL_PEOPLE,
  EDITORIAL_PRINCIPLES,
  REVIEW_CADENCE,
} from "@/content/editorial";
import { lastmodFor } from "@/lib/seo/sitemap";

const PATH = "/redaktionell-policy";

export const Route = createFileRoute("/redaktionell-policy")({
  head: () => {
    const title = "Redaktionell policy — så granskar vi fakta | Fenny";
    const description =
      "Vem skriver Fennys guider, hur ofta granskas de och vilka källor använder vi? Här är vår redaktionella policy, granskningsintervall och rutin för rättelser.";
    const modified = lastmodFor(PATH);

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Redaktionell policy", path: PATH },
    ]);

    const aboutLd = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${abs(PATH)}#page`,
      url: abs(PATH),
      name: title,
      description,
      inLanguage: "sv-SE",
      publisher: { "@id": ORG_ID },
      mainEntity: { "@id": ORG_ID },
      ...(modified && { dateModified: modified }),
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [{ rel: "canonical", href: abs(PATH) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/redaktionell-policy"})) },
        { type: "application/ld+json", children: JSON.stringify(aboutLd) },
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        {
          type: "application/ld+json",
          children: JSON.stringify({ ...speakable([".lede"]), url: abs(PATH) }),
        },
      ],
    };
  },
  component: EditorialPolicyPage,
});

function EditorialPolicyPage() {
  const people = Object.values(EDITORIAL_PEOPLE);

  return (
    <main>
      <Section>
        <article className="mx-auto max-w-3xl">
          <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
            Redaktionell policy
          </h1>
          <p className="lede mt-5 text-lg text-fg/75" data-speakable>
            Fennys guider, prisspann och jämförelser skrivs av teamet som gör
            filmerna. Vi bygger på våra egna produktioner, anger källa när vi
            använder extern statistik och visar alltid när en sida senast
            granskades.
          </p>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold">Vem skriver</h2>
            <ul className="mt-4 space-y-4">
              {people.map((p) => (
                <li
                  key={p.name}
                  className="rounded-xl border border-fg/10 p-4"
                >
                  <div className="font-medium">{p.name}</div>
                  <div className="text-sm text-fg/60">{p.role}</div>
                  {p.credentials && (
                    <p className="mt-2 text-sm text-fg/75">{p.credentials}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold">Så granskar vi</h2>
            <dl className="mt-4 space-y-5">
              {EDITORIAL_PRINCIPLES.map((p) => (
                <div key={p.title}>
                  <dt className="font-medium">{p.title}</dt>
                  <dd className="mt-1 text-sm text-fg/75">{p.body}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold">Granskningsintervall</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-fg/15">
                    <th className="py-2 pr-4 font-medium">Innehållstyp</th>
                    <th className="py-2 font-medium">Granskas</th>
                  </tr>
                </thead>
                <tbody>
                  {REVIEW_CADENCE.map((r) => (
                    <tr key={r.type} className="border-b border-fg/10">
                      <td className="py-2 pr-4">{r.type}</td>
                      <td className="py-2 text-fg/75">{r.interval}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-2xl font-semibold">Rättelser och kontakt</h2>
            <p className="mt-3 text-fg/75">
              Ser du något som inte stämmer? Mejla{" "}
              <a
                href={CONTACT.emailMailto}
                className="underline underline-offset-2"
              >
                {CONTACT.email}
              </a>{" "}
              så rättar vi och uppdaterar granskningsdatumet. Journalister som
              vill ha citat eller underlag hittar mer på{" "}
              <Link to="/for-journalister" className="underline underline-offset-2">
                För journalister
              </Link>
              .
            </p>
          </section>
        </article>
      </Section>
    </main>
  );
}
